import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import { useNavigate } from "react-router"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"

import useT from "utils/hooks/useT"
import { Popup } from "components"
import ESignatureModal from "components/Lims/ESignatureModal"
import CertificateOfAnalysis from "components/Lims/CertificateOfAnalysis"
import { openPresignedFile } from "utils/s3Download"
import {
  limsResults,
  limsSampleQa,
  limsRelease,
  limsSampleQc,
  limsBatchHold,
  limsOos,
  limsReruns,
  limsProductTestPlan,
  sampleView,
  analysisDefinitions as analysisDefinitionsActions,
  message,
} from "state_management"
import { QA_DECISION } from "state_management/lims/limsResults"

// QA SampleReview (F6, SPEC-038) — REPLACES QA Analysis Review in the LIMS menu. QA disposition is
// now at sample/lot level: this worklist lists every samplenr/lotnr that is COMPLETE (all required
// analyses across all its analysis runs have cleared review, no open OOS/OOT investigation, all
// reruns finished — limsSampleQa.selectQaSampleWorklist). Per row QA can:
//   - "Open in Sample View" — deep-links to the Sample View filtered to the samplenr/lotnr, the
//     GENGENNEMGANG (review) surface: per sample-analysis "go to analysis" → audit trail (F2) +
//     comments, and the whole sample's history. There is NO sign in the Sample View.
//   - Approve & release — the QA e-signature lands HERE (capability "qa", 4-eyes ≠ enterers +
//     reviewers + supervisors). On approval the lot is released and a certificate is issued; the
//     row moves to "Released" with a certificate-download link.
//   - Reject — places a signed batch hold on the lot (the whole batch is blocked from sale).
//
// The SERVER re-evaluates the whole completeness/release gate at signing time (release.py) — this
// screen's worklist + four-eyes are the fail-fast UX on top of that authoritative gate.
export default function QaSampleReview() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const sampleGroupsRaw = useSelector((s) => s.sampleView.sampleGroups, shallowEqual)
  const sampleGroups = useMemo(() => sampleGroupsRaw || [], [sampleGroupsRaw])
  const resItems = useSelector((s) => s.limsResults.items, shallowEqual)
  const relItems = useSelector((s) => s.limsRelease.items, shallowEqual)
  const sampleQc = useSelector((s) => s.limsSampleQc, shallowEqual)
  const batchHoldItems = useSelector((s) => s.limsBatchHold.items, shallowEqual)
  const oosItems = useSelector((s) => s.limsOos.items, shallowEqual)
  const rerunItems = useSelector((s) => s.limsReruns.items, shallowEqual)
  const productTestPlan = useSelector((s) => s.limsProductTestPlan, shallowEqual)
  // Required-analysis resolution (Product Test Plan → definition names) reads the definitions map.
  const analysisDefs = useSelector((s) => s.analysisDefinitions, shallowEqual)
  const members = useSelector((s) => s.members)

  const [releasing, setReleasing] = useState(null) // the worklist row pending an Approve & release sig
  const [rejecting, setRejecting] = useState(null) // the worklist row pending a Reject (batch hold) sig
  const [saving, setSaving] = useState(false)
  const [viewingCert, setViewingCert] = useState(null) // the released group whose printable CoA is open

  useEffect(() => {
    // SPEC-021 (B3): QC capabilities ride on the member dump (loaded app-wide) — no fetch here.
    dispatch(limsResults.fetch())
    dispatch(sampleView.fetch())
    dispatch(limsRelease.fetch())
    dispatch(limsSampleQc.fetch())
    dispatch(limsBatchHold.fetch())
    dispatch(limsOos.fetch())
    dispatch(limsReruns.fetch())
    dispatch(limsProductTestPlan.fetch())
    // Required-analysis resolution (Product Test Plan) needs the analysis definitions.
    dispatch(analysisDefinitionsActions.fetch())
  }, [dispatch])

  // A minimal state slice the completeness/worklist + release selectors read (so the memoized
  // selectors recompute only when these collections change).
  const miniState = useMemo(
    () => ({
      sampleView: { sampleGroups },
      limsResults: { items: resItems },
      limsRelease: { items: relItems },
      limsSampleQc: sampleQc,
      limsBatchHold: { items: batchHoldItems },
      limsOos: { items: oosItems },
      limsReruns: { items: rerunItems },
      limsProductTestPlan: productTestPlan,
      analysisDefinitions: analysisDefs,
    }),
    [sampleGroups, resItems, relItems, sampleQc, batchHoldItems, oosItems, rerunItems, productTestPlan, analysisDefs]
  )

  // The worklist of COMPLETE samplenr/lotnr awaiting a QA decision.
  const worklist = useMemo(() => limsSampleQa.selectQaSampleWorklist(miniState), [miniState])

  // Released rows: one per release record, joined back to its Sample-View group (so the certificate
  // can read the analyses); falls back to the release record's own fields if the group is gone.
  const releasedRows = useMemo(() => {
    const groupByKey = buildGroupByObservation(sampleGroups)
    return relItems.map((rel) => ({
      rel,
      group:
        groupByKey[rel.id] || {
          productName: rel.productName,
          sampleName: rel.sampleName,
          observationId: rel.observationId,
          analyses: [],
        },
    }))
  }, [sampleGroups, relItems])

  const initials = (id) => (id && members[id] && members[id].initials) || "—"

  // Four-eyes on the release signature: everyone who entered / reviewed / supervised / override-signed
  // one of the group's QC results may not also sign the release (release.py enforces this too).
  const releaseExclusions = useMemo(
    () => (releasing ? limsRelease.selectReleaseExclusions(miniState, releasing.group) : []),
    [releasing, miniState]
  )

  // The lot(s) registered on the rejecting group's sample-analyses — a Reject places a hold on them.
  const rejectLots = useMemo(
    () => (rejecting ? limsRelease.selectGroupBatchLots(miniState, rejecting.group) : []),
    [rejecting, miniState]
  )

  // The releasing group's results still awaiting a QA decision (sent-to-qa). Approving the lot
  // commits a per-sample APPROVE on each of these under the QA signature BEFORE the release — the
  // SPEC-038 model moves the QA approval to the sample/lot level (no per-analysis QA trigger), but
  // the authoritative release gate (release.py) still requires every result qa-approved, so the
  // lot-level "Approve" drives them there in one signed step.
  const pendingForReleasing = useMemo(() => {
    if (!releasing) return []
    const sampleIds = new Set((releasing.group.analyses || []).map((a) => a.sampleId).filter(Boolean))
    return resItems.filter((r) => sampleIds.has(r.sampleId) && r.state === "sent-to-qa")
  }, [releasing, resItems])

  // Approve & release under ONE QA signature: first commit a per-sample APPROVE on every still-
  // pending (sent-to-qa) result in the group (server verifies the QA password + capability + 4-eyes
  // each time), then release the lot. Sequential so every approval lands before the release gate
  // re-evaluates. Returns the promise so the dialog shows a backend 403 (password / capability /
  // four-eyes) or 422 (server release gate) inline; a no-longer-READY lot surfaces its own message.
  const handleRelease = (signature) => {
    const row = releasing
    setSaving(true)
    const approveAll = pendingForReleasing.reduce(
      (chain, r) => chain.then(() => dispatch(limsResults.setQaDecision(r.id, QA_DECISION.APPROVE, signature))),
      Promise.resolve()
    )
    return approveAll
      .then(() => dispatch(limsRelease.release(row.group, signature)))
      .then(() => {
        setReleasing(null)
        dispatch(message.success(t("limsReleaseDone")))
      })
      .catch((err) => {
        if (limsRelease.isReleaseNotReadyError(err)) err.message = t("limsReleaseNotReady")
        throw err
      })
      .finally(() => setSaving(false))
  }

  // The rejecting group's results still awaiting a QA decision — a lot rejection records a per-sample
  // STOP on each (the QA verdict on the work), then holds the lot.
  const pendingForRejecting = useMemo(() => {
    if (!rejecting) return []
    const sampleIds = new Set((rejecting.group.analyses || []).map((a) => a.sampleId).filter(Boolean))
    return resItems.filter((r) => sampleIds.has(r.sampleId) && r.state === "sent-to-qa")
  }, [rejecting, resItems])

  // Reject the lot under ONE QA signature: commit a per-sample STOP on every still-pending result
  // (the auditable QA verdict on the work), then place a signed batch hold on each registered lot
  // (the BATCH_REJECTED block on the whole lot). Blocked before opening the dialog when no lot is
  // registered — without one there is nothing to hold (mirrors the per-result STOP guard, SPEC-020
  // C3). Returns the promise so the dialog shows 403 inline.
  const handleReject = (signature) => {
    setSaving(true)
    const stopAll = pendingForRejecting.reduce(
      (chain, r) => chain.then(() => dispatch(limsResults.setQaDecision(r.id, QA_DECISION.STOP, signature))),
      Promise.resolve()
    )
    const commit = rejectLots.reduce(
      (chain, lot) => chain.then(() => dispatch(limsBatchHold.hold(lot, { signature }))),
      stopAll
    )
    return commit
      .then(() => {
        setRejecting(null)
        dispatch(message.success(t("limsSampleQaRejectedDone", { sample: rejecting.sampleName || "—" })))
      })
      .finally(() => setSaving(false))
  }

  // Deep-link to the Sample View, filtered to this samplenr/lotnr — the review surface (no sign).
  const openInSampleView = (row) => {
    navigate(`/sample-view?sample=${encodeURIComponent(row.sampleName || "")}`)
  }

  // Download the released lot's certificate: prefer the SERVER-generated certificate (presigned GET
  // from the qaReports area); fall back to the client-rendered printable CoA when none was stored
  // (the qaReports bucket was not provisioned at release time).
  const downloadCertificate = (rel, group) => {
    if (limsRelease.hasStoredCertificate(rel)) {
      dispatch(limsRelease.certificateDownloadUrl(rel))
        .then((url) => {
          if (url) openPresignedFile(url)
          else setViewingCert(group)
        })
        .catch(() => setViewingCert(group))
      return
    }
    setViewingCert(group)
  }

  return (
    <div className="LimsQueue">
      <h1 className="LimsQueue-title">{t("limsQaSampleReview")}</h1>

      <section className="LimsQueue-section">
        <h2 className="LimsQueue-subtitle">{t("limsSampleQaPendingHeading")}</h2>
        <p className="LimsQueue-intro">{t("limsSampleQaIntro")}</p>
        {worklist.length === 0 ? (
          <p className="LimsQueue-empty">{t("limsSampleQaEmpty")}</p>
        ) : (
          <Table striped hover responsive className="LimsQueue-table">
            <thead>
              <tr>
                <th>{t("limsCoaProduct")}</th>
                <th>{t("limsCoaSample")}</th>
                <th>{t("limsCoaSampleId")}</th>
                <th>{t("limsSampleQaSignedAnalyses")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {worklist.map((row) => (
                <tr key={row.key}>
                  <td>{row.productName || t("none")}</td>
                  <td>{row.sampleName || "—"}</td>
                  <td>{row.observationId != null && row.observationId !== "" ? row.observationId : "—"}</td>
                  <td>{row.signedCount}</td>
                  <td className="LimsQueue-actions">
                    <div className="LimsQueue-actions-inner">
                      <Button variant="outline-secondary" size="sm" onClick={() => openInSampleView(row)}>
                        {t("limsSampleQaOpenInSampleView")}
                      </Button>
                      <Button variant="success" size="sm" disabled={saving} onClick={() => setReleasing(row)}>
                        {t("limsSampleQaApprove")}
                      </Button>
                      <Button variant="outline-danger" size="sm" disabled={saving} onClick={() => setRejecting(row)}>
                        {t("limsSampleQaReject")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      <section className="LimsQueue-section">
        <h2 className="LimsQueue-subtitle">{t("limsReleasedHeading")}</h2>
        {releasedRows.length === 0 ? (
          <p className="LimsQueue-empty">{t("limsReleasedEmpty")}</p>
        ) : (
          <Table striped hover responsive className="LimsQueue-table">
            <thead>
              <tr>
                <th>{t("limsCoaProduct")}</th>
                <th>{t("limsCoaSample")}</th>
                <th>{t("limsCoaSampleId")}</th>
                <th>{t("limsCoaReleasedBy")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {releasedRows.map(({ rel, group }) => (
                <tr key={rel.id}>
                  <td>{group.productName || t("none")}</td>
                  <td>{group.sampleName || "—"}</td>
                  <td>{group.observationId != null && group.observationId !== "" ? group.observationId : "—"}</td>
                  <td>{initials(rel.releasedBy)}</td>
                  <td className="LimsQueue-actions">
                    <div className="LimsQueue-actions-inner">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => downloadCertificate(rel, group)}
                      >
                        {t("limsSampleQaDownloadCertificate")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      {releasing && (
        <ESignatureModal
          title={t("limsSampleQaApprove")}
          requiredCapability="qa"
          excludedMemberIds={releaseExclusions}
          commentContext="qa-release"
          onSign={handleRelease}
          onCancel={() => setReleasing(null)}
        />
      )}

      {rejecting && rejectLots.length === 0 && (
        <Popup nonScrollable title={t("limsSampleQaReject")} onCancel={() => setRejecting(null)}>
          <p className="LimsQueue-intro">{t("limsSampleQaRejectNoLot")}</p>
          <div className="ResultEntryPanel-qa-actions">
            <Button variant="light" onClick={() => setRejecting(null)}>
              {t("close")}
            </Button>
          </div>
        </Popup>
      )}

      {rejecting && rejectLots.length > 0 && (
        <ESignatureModal
          title={t("limsSampleQaReject")}
          requiredCapability="qa"
          requireReason
          excludedMemberIds={releaseExclusions}
          commentContext="qa-release"
          onSign={handleReject}
          onCancel={() => setRejecting(null)}
        />
      )}

      {viewingCert && (
        <Popup title={t("limsCoaTitle")} onCancel={() => setViewingCert(null)}>
          <CertificateOfAnalysis group={viewingCert} />
        </Popup>
      )}
    </div>
  )
}

// Index the Sample-View groups by the release key (productName|sampleName|observationId), each
// carrying only that observation's analyses — so a released record (keyed by the compound id) can
// be joined back to its group for the certificate. First write per key wins (stable).
function buildGroupByObservation(sampleGroups) {
  const byKey = {}
  ;(sampleGroups || []).forEach((g) => {
    const observations = new Set((g.analyses || []).map((a) => String(a.observationId ?? g.observationId ?? "")))
    observations.forEach((obs) => {
      const key = `${g.productName || ""}|${g.sampleName || ""}|${obs}`
      if (!byKey[key]) {
        byKey[key] = {
          ...g,
          observationId: obs,
          analyses: (g.analyses || []).filter((x) => String(x.observationId ?? g.observationId ?? "") === obs),
        }
      }
    })
  })
  return byKey
}
